// Table exports used by the list pages (tm-admin-web-react has CSV / PDF /
// Excel / Print on its lists). `head` = column labels, `body` = rows of text.

const stamp = () => new Date().toISOString().slice(0, 10);

const save = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const csvCell = (value) => {
  const text = String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const downloadCsv = (name, head, body) => {
  const lines = [head, ...body].map((row) => row.map(csvCell).join(","));
  // BOM so Excel opens UTF-8 (symbols, Bangla) correctly
  save(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }), `${name}-${stamp()}.csv`);
};

export const downloadExcel = async (name, head, body) => {
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.aoa_to_sheet([head, ...body]);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, name.slice(0, 31));
  XLSX.writeFile(book, `${name}-${stamp()}.xlsx`);
};

export const downloadPdf = async (name, title, head, body) => {
  const [{ default: JsPdf }, { default: autoTable }] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ]);
  const doc = new JsPdf({ orientation: head.length > 6 ? "landscape" : "portrait" });
  doc.setFontSize(14);
  doc.text(title, 14, 14);
  autoTable(doc, {
    head: [head],
    body,
    startY: 20,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [15, 157, 88] },
  });
  doc.save(`${name}-${stamp()}.pdf`);
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Prints through a hidden iframe so the app's own page isn't printed
export const printHtml = (title, bodyHtml) => {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(frame);

  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><title>${escapeHtml(title)}</title><style>
    body{font-family:Arial,sans-serif;font-size:12px;color:#111;padding:16px}
    h2{margin:0 0 12px;font-size:16px}
    table{border-collapse:collapse;width:100%}
    th,td{border:1px solid #bbb;padding:5px 7px;text-align:left}
    th{background:#eee}
    .row{display:flex;justify-content:space-between;border-bottom:1px solid #ddd;padding:6px 0}
  </style></head><body>${bodyHtml}</body></html>`);
  doc.close();

  frame.contentWindow.focus();
  setTimeout(() => {
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 250);
};

// A document for paper: A4 with even margins, page breaks the caller controls (css), printed through its own frame
// so none of the app (sidebar, buttons) is on the page. `css` is added after the base look.
export const printDocument = (title, bodyHtml, css = "") => {
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(frame);

  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
    @page { size: A4; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #bbb; padding: 5px 7px; text-align: left; vertical-align: top; }
    ${css}
  </style></head><body>${bodyHtml}</body></html>`);
  doc.close();

  frame.contentWindow.focus();
  setTimeout(() => {
    frame.contentWindow.print();
    setTimeout(() => frame.remove(), 1000);
  }, 300);
};

export const printTable = (title, head, body) =>
  printHtml(
    title,
    `<h2>${escapeHtml(title)}</h2><table><thead><tr>${head.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead><tbody>${body
      .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("")}</tbody></table>`
  );

// Label / value pairs (e.g. a cash receipt)
export const printDetails = (title, pairs) =>
  printHtml(
    title,
    `<h2>${escapeHtml(title)}</h2>${pairs
      .map(([label, value]) => `<div class="row"><span>${escapeHtml(label)}</span><strong>${escapeHtml(value)}</strong></div>`)
      .join("")}`
  );
